import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from "react-redux";
import Swal from "sweetalert2";
import { Modal, Pagination } from 'react-bootstrap';

import LocationAutocomplete from '../components/Property/components/LocationAutocomplete';

import { getUsersAction, deleteUserAction, createUserAction, updateUserAction } from '../../store/actions/user/UserActions';
import { getAllRolesAction } from '../../store/actions/role/RoleActions';
import { getBanksAction } from '../../store/actions/bank/BankActions';

const VERIFICATION_AGENT_ROLE_ID = process.env.REACT_APP_VERIFICATION_AGENT_ID;
const BANK_VERIFICATION_AGENT_ROLE_ID = process.env.REACT_APP_BANK_VERIFICATION_AGENT_ID;

const User = () => {
	const dispatch = useDispatch();

	const { users, loading } = useSelector((state) => state.users);
	const { roles } = useSelector((state) => state.roles);
	const { banks } = useSelector((state) => state.bank);
	const bankRows = banks?.rows || [];
	const roleRows = roles?.rows || [];
	const userRows = users?.rows || [];
	const totalPages = users?.pages || 1;
	const [formErrors, setFormErrors] = useState({});
	const [searchTerm, setSearchTerm] = useState('');
	const [location, setLocation] = useState({
		'latitude' : '',
		'longitude' : ''
	});
	const [radius, setRadius] = useState(10)
	const [bank, setBank] = useState('')

	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize] = useState(10);
	const [addCard, setAddCard] = useState(false);

	const [addFormData, setAddFormData] = useState({
		name: '',
		email: '',
		password: '',
		userRole: '',
		isEnabled: true
	});

	const [showFilterPopup, setShowFilterPopup] = useState(false);
	const filterButtonRef = useRef(null);
	const filterPopupRef = useRef(null);

	const [filterValues, setFilterValues] = useState({
		name: '',
		email: '',
		role: '',
		status: ''
	});

	const [editMode, setEditMode] = useState(false);
	const [editUserData, setEditUserData] = useState(null);

	const handleAddFormChange = (event) => {
		const { name, value } = event.target;

		switch (name) {
			case 'latitude':
				setLocation((prev) => ({
					...prev,
					latitude: value
				}));
				setFormErrors((prevErrors) => ({
					...prevErrors,
					latitude: ''
				}));
				break;

			case 'longitude':
				setLocation((prev) => ({
					...prev,
					longitude: value
				}));
				setFormErrors((prevErrors) => ({
					...prevErrors,
					longitude: ''
				}));
				break;

			case 'radius':
				setRadius(value);
				setFormErrors((prevErrors) => ({
					...prevErrors,
					radius: ''
				}));
				break;

			default:
				setAddFormData((prev) => ({
					...prev,
					[name]: name === "isEnabled" ? value === "true" : value
				}));
				setFormErrors((prevErrors) => ({
					...prevErrors,
					[name]: ''
				}));
				break;
		}
	};


	const handleAddFormSubmit = async (event) => {
		event.preventDefault();

		const { name, email, userRole, password } = addFormData;
		const errors = {};

		if (!name.trim()) errors.name = "Name is required.";
		if (!email.trim()) errors.email = "Email is required.";
		else if (!/\S+@\S+\.\S+/.test(email)) errors.email = "Email is invalid.";
		if (!userRole) errors.userRole = "Role is required.";
		if (!editMode && !password.trim()) errors.password = "Password is required.";

		if (addFormData.userRole === VERIFICATION_AGENT_ROLE_ID || addFormData.userRole === BANK_VERIFICATION_AGENT_ROLE_ID) {
			if (!location.latitude) {
				errors.latitude = 'Latitude is required';
			}
			if (!location.longitude) {
				errors.longitude = 'Longitude is required';
			}
			if (!radius || isNaN(radius) || Number(radius) < 10) {
				errors.radius = 'Radius must be a number and at least 10';
			}
		}

		if(addFormData.userRole === BANK_VERIFICATION_AGENT_ROLE_ID) {
			if (!bank.trim()) errors.bank = "Bank is required.";
		}

		setFormErrors(errors);

		if (Object.keys(errors).length > 0) return;

		const userPayload = {
			name,
			email,
			userRole,
			isEnabled: addFormData.isEnabled
		};

		if (addFormData.userRole === VERIFICATION_AGENT_ROLE_ID || addFormData.userRole === BANK_VERIFICATION_AGENT_ROLE_ID) {
			const loc  = {
				"lat":location.latitude,
				"lng":location.longitude,
				"radius":parseInt(radius)
			}
			userPayload.location = loc;
		}
		if(addFormData.userRole === BANK_VERIFICATION_AGENT_ROLE_ID) {
			userPayload.bankId = bank
		}
		console.log(userPayload)
		if (password) userPayload.password = password;

		try {
			let response;
			if (editMode && editUserData?._id) {
				response = await dispatch(updateUserAction(editUserData._id, userPayload));
			} else {
				response = await dispatch(createUserAction(userPayload));
			}

			if (response.data.success) {
				Swal.fire({ icon: 'success', title: 'Success!', text: editMode ? 'User updated' : 'User added' });
				resetForm();
				dispatch(getUsersAction(currentPage, pageSize, searchTerm));
			} else {
				Swal.fire({ icon: 'error', title: 'Error', text: response.data?.msg || 'Something went wrong' });
			}
		} catch (error) {
			Swal.fire({ icon: 'error', title: 'Error', text: error?.response?.data?.msg || 'Unexpected error occurred' });
		}
	};

	const handleDelete = async (userId) => {
		const result = await Swal.fire({
			title: "Are you sure?",
			text: "You won't be able to revert this!",
			icon: "warning",
			showCancelButton: true,
			confirmButtonColor: "#3085d6",
			cancelButtonColor: "#d33",
			confirmButtonText: "Yes, delete it!"
		});

		if (result.isConfirmed) {
			const response = await dispatch(deleteUserAction(userId));
			if (response.data.success) {
				Swal.fire("Deleted!", "The user has been deleted.", "success");
				dispatch(getUsersAction(currentPage, pageSize, searchTerm));
			}
		}
	};

	const handleEdit = (user) => {
		setEditMode(true);
		setEditUserData(user);
		setFormErrors({});
		setAddFormData({
			name: user.name,
			email: user.email,
			password: '',
			userRole: user.userRole?._id || '',
			isEnabled: user.isEnabled
		});
		if (user.userRole?._id === VERIFICATION_AGENT_ROLE_ID || user.userRole?._id === BANK_VERIFICATION_AGENT_ROLE_ID) {
			setRadius(user?.location?.radius)
			const updated = {
				latitude: '',
				longitude: ''
			};
			updated.latitude = user?.location?.lat;
			updated.longitude = user?.location?.lng;
			setLocation(updated);
		}
		if (user.userRole?._id === BANK_VERIFICATION_AGENT_ROLE_ID) {
			console.log(user?.bankId)
			setBank(user?.bankId)
		}
		setAddCard(true);
	};

	const resetForm = () => {
		setAddCard(false);
		setEditMode(false);
		setEditUserData(null);
		setAddFormData({
			name: '',
			email: '',
			password: '',
			userRole: '',
			isEnabled: true
		});
	};

	const searchUser = () => {
		dispatch(getUsersAction(currentPage, pageSize, searchTerm));
	}

	const buildFilterQuery = () => {
		const params = new URLSearchParams();

		if (filterValues.name) {
			params.append('filter_name', `regex-${filterValues.name}`);
		}

		if (filterValues.email) {
			params.append('filter_email', `regex-${filterValues.email}`);
		}

		if (filterValues.role) {
			params.append('filter_userRole', filterValues.role); // Assuming role is the ID like '681aea98f5a25f1b4495fb30'
		}

		if (filterValues.status) {
			params.append('filter_isEnabled', filterValues.status); // Should be 'true' or 'false' string
		}

		return params.toString(); // Returns the full query string
	};

	const applyFilters = () => {
		const filterQuery = buildFilterQuery();
		dispatch(getUsersAction(currentPage, pageSize, searchTerm, filterQuery));
		setShowFilterPopup(false);
	};

	const resetFilters = () => {
		dispatch(getUsersAction(currentPage, pageSize, searchTerm));
		setFilterValues({ name: '', email: '', role: '', status: '' });
		setShowFilterPopup(false);
	};

	const handleRoleChange = (e) => {
		if (e.target.value !== VERIFICATION_AGENT_ROLE_ID || e.target.value !== BANK_VERIFICATION_AGENT_ROLE_ID) {
			setFormErrors(prev => {
				const { latitude, longitude, radius, ...rest } = prev;
				return rest;
			});
		}
	};

	const onPlaceSelected = async (place) => {
        const updated = {
            latitude: '',
            longitude: ''
        };
        if (place.geometry?.location) {
            updated.latitude = place.geometry.location.lat();
            updated.longitude = place.geometry.location.lng();
        }
        setLocation(updated);
		setFormErrors((prevErrors) => ({
			...prevErrors,
			latitude: '',
			longitude: ''
		}));
    }

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
			filterPopupRef.current &&
			!filterPopupRef.current.contains(event.target) &&
			!filterButtonRef.current.contains(event.target)
			) {
			setShowFilterPopup(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	useEffect(() => {
		dispatch(getUsersAction(currentPage, pageSize, searchTerm));
	}, [dispatch, currentPage, pageSize, searchTerm]);

	useEffect(() => {
		dispatch(getAllRolesAction());
		dispatch(getBanksAction(1,100,"","filter_isEnabled=true"))
	}, [dispatch]);

	return (
		<>
			<div className="col-12">
				<Modal className="modal fade" show={addCard} onHide={resetForm}>
					<form onSubmit={handleAddFormSubmit}>
						<div className="modal-header">
							<h4 className="modal-title fs-20">{editMode ? 'Edit User' : 'Add User'}</h4>
							<button type="button" className="btn-close" onClick={resetForm}></button>
						</div>
						<div className="modal-body">
							<div className="add-contact-box">
								<div className="add-contact-content">
									<div className="form-group mb-3">
										<label>Name</label>
										<input type="text" className={`form-control ${formErrors.name ? 'is-invalid' : ''}`} name="name" value={addFormData.name} onChange={handleAddFormChange} placeholder="Name"  />
										{formErrors.name && <div className="invalid-feedback">{formErrors.name}</div>}
									</div>
									<div className="form-group mb-3">
										<label>Email</label>
										<input type="email" className={`form-control ${formErrors.email ? 'is-invalid' : ''}`} name="email" value={addFormData.email} onChange={handleAddFormChange} placeholder="Email"  />
										{formErrors.email && <div className="invalid-feedback">{formErrors.email}</div>}
									</div>
									<div className="form-group mb-3">
										<label>Role</label>
										<select 
											name="userRole" 
											className={`form-control ${formErrors.userRole ? 'is-invalid' : ''}`} 
											value={addFormData.userRole} 
											onChange={(e)=>{handleAddFormChange(e);handleRoleChange(e)}} 
										>
											<option value="">Select Role</option>
											{roleRows.map((role) => (
												<option key={role._id} value={role._id}>{role.name}</option>
											))}
										</select>
										{formErrors.userRole && <div className="invalid-feedback">{formErrors.userRole}</div>}
									</div>
									{(addFormData.userRole === BANK_VERIFICATION_AGENT_ROLE_ID) && (
										<div className="form-group mb-3">
											<label>Select Bank</label>
											<select 
												name="bank" 
												className={`form-control ${formErrors.bank ? 'is-invalid' : ''}`} 
												value={bank} 
												onChange={(e)=>{setBank(e.target.value);}} 
											>
												<option value="">Select Bank</option>
												{bankRows.map((bank) => (
													<option key={bank._id} value={bank._id}>{bank.name}</option>
												))}
											</select>
											{formErrors.bank && <div className="invalid-feedback">{formErrors.bank}</div>}
										</div>
									)}
									{(addFormData.userRole === VERIFICATION_AGENT_ROLE_ID || addFormData.userRole === BANK_VERIFICATION_AGENT_ROLE_ID) && (
										<>
											<div className="form-group mb-3">
												<label>Select Location</label>
												<LocationAutocomplete
													onPlaceSelected={onPlaceSelected}
													onCleared={() => setLocation({ latitude: '', longitude: '' })}
												/>
											</div>
											<div className="form-group mb-3">
												<label>Latitude</label>
												<input
													type="text"
													className={`form-control ${formErrors.latitude ? 'is-invalid' : ''}`}
													name="latitude"
													value={location.latitude}
													onChange={handleAddFormChange}
													placeholder="Latitude"
												/>
												{formErrors.latitude && <div className="invalid-feedback">{formErrors.latitude}</div>}
											</div>
											<div className="form-group mb-3">
												<label>Longitude</label>
												<input 
													type="text" 
													className={`form-control ${formErrors.longitude ? 'is-invalid' : ''}`} 
													name="longitude" 
													value={location.longitude} 
													onChange={handleAddFormChange}
													placeholder="Longitude"  
												/>
												{formErrors.longitude && <div className="invalid-feedback">{formErrors.longitude}</div>}
											</div>
											<div className="form-group mb-3">
												<label>Radius</label>
												<input
													type="number"
													min={10}
													className={`form-control ${formErrors.radius ? 'is-invalid' : ''}`}
													name="radius"
													value={radius}
													onChange={handleAddFormChange}
													placeholder="Radius"
												/>
												{formErrors.radius && <div className="invalid-feedback">{formErrors.radius}</div>}
											</div>
										</>
									)}
									<div className="form-group mb-3">
										<label>Status</label>
										<select name="isEnabled" className={`form-control ${formErrors.isEnabled ? 'is-invalid' : ''}`} value={addFormData.isEnabled} onChange={handleAddFormChange}>
											<option value={true}>Enabled</option>
											<option value={false}>Disabled</option>
										</select>
										{formErrors.isEnabled && <div className="invalid-feedback">{formErrors.isEnabled}</div>}
									</div>
									<div className="form-group mb-3">
										<label>Password {editMode && "(Leave blank to keep current)"}</label>
										<input type="password" className={`form-control ${formErrors.password ? 'is-invalid' : ''}`} name="password" value={addFormData.password} onChange={handleAddFormChange} placeholder="Password" />
										{formErrors.password && <div className="invalid-feedback">{formErrors.password}</div>}
									</div>
								</div>
							</div>
						</div>
						<div className="modal-footer">
							<button type="submit" className="btn btn-primary">{editMode ? "Update" : "Add"}</button>
							<button type="button" onClick={resetForm} className="btn btn-danger">Discard</button>
						</div>
					</form>
				</Modal>

				<div className="card">
					<div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-3">
						<h4 className="card-title mb-0">User List</h4>
						
						<div className="input-group w-50">
							<input
								type="text"
								className="form-control"
								placeholder="Search by name or email..."
								onChange={(e) => setSearchTerm(e.target.value)} 
								onKeyDown={(e) => {
									if (e.key === 'Enter') {
										searchUser();
									}
								}}
							/>
							<span onClick={searchUser} className="input-group-text" style={{ cursor: 'pointer' }}>
								<i className="fa fa-search"></i>
							</span>
						</div>

						{/* Filter Icon */}
						<div className="position-relative">
							<button
								className="btn btn-outline-secondary shadow btn-xs sharp me-2"
								onClick={() => setShowFilterPopup((prev) => !prev)}
								ref={filterButtonRef}
							>
								<i className="fa fa-filter"></i>
							</button>

							{/* Filter Popup */}
							{showFilterPopup && (
								<div
									className="card p-3 shadow position-absolute"
									style={{
										right: '0',   
										zIndex: 1000,
										width: '300px',
										height: '245px'
									}}
									ref={filterPopupRef}
								>
									{/* Filter Fields */}
									<input
										type="text"
										className="form-control mb-2"
										placeholder="Name"
										value={filterValues.name}
										onChange={(e) => setFilterValues({ ...filterValues, name: e.target.value })}
									/>

									<input
										type="text"
										className="form-control mb-2"
										placeholder="Email"
										value={filterValues.email}
										onChange={(e) => setFilterValues({ ...filterValues, email: e.target.value })}
									/>

									<select
										className="form-control mb-2"
										value={filterValues.role}
										onChange={(e) => setFilterValues({ ...filterValues, role: e.target.value })}
									>
										<option value="">All Roles</option>
										{roleRows.map((role) => (
											<option key={role._id} value={role._id}>{role.name}</option>
										))}
									</select>

									<select
										className="form-control mb-3"
										value={filterValues.status}
										onChange={(e) => setFilterValues({ ...filterValues, status: e.target.value })}
									>
										<option value="">All Statuses</option>
										<option value="true">Enabled</option>
										<option value="false">Disabled</option>
									</select>

									<div className="d-flex justify-content-between">
										<button className="btn btn-secondary" onClick={resetFilters}>Reset</button>
										<button className="btn btn-primary" onClick={applyFilters}>Apply</button>
									</div>
								</div>
							)}
						</div>

						<button className="btn btn-primary shadow btn-xs sharp me-2" onClick={() => setAddCard(true)}>
							<i className="fa fa-plus"></i>
						</button>
					</div>
					<div className="card-body">
						<div className="table-responsive">
							<table className="table table-striped">
								<thead>
									<tr>
										<th>Name</th>
										<th>Email</th>
										<th>Role</th>
										<th>Enabled</th>
										<th>Action</th>
									</tr>
								</thead>
								<tbody>
									{loading ? (
										Array.from({ length: 5 }).map((_, idx) => (
											<tr key={idx}>
												<td colSpan="5">
													<div className="skeleton-loader" style={{ height: 30, width: '100%', background: '#eee', borderRadius: 4 }}></div>
												</td>
											</tr>
										))
									) : userRows.length === 0 ? (
										<tr><td colSpan="5">No users found.</td></tr>
									) : (
										userRows.map((user) => (
											<tr key={user._id}>
												<td>{user.name}</td>
												<td>{user.email}</td>
												<td>{user.userRole?.name || ''}</td>
												<td className={user.isEnabled ? 'text-success' : 'text-danger'}>
													{user.isEnabled ? 'True' : 'False'}
												</td>
												<td>
													<div className="d-flex">
														<button className="btn btn-secondary shadow btn-xs sharp me-2" onClick={() => handleEdit(user)}>
															<i className="fa fa-pencil"></i>
														</button>
														<button className="btn btn-danger shadow btn-xs sharp" onClick={() => handleDelete(user._id)}>
															<i className="fa fa-trash"></i>
														</button>
													</div>
												</td>
											</tr>
										))
									)}
								</tbody>
							</table>
						</div>

						<div className="d-flex justify-content-between align-items-center mt-4">
							<div></div> {/* Empty div to align right side content */}
							<div className="d-flex align-items-center gap-3">
								<span className="text-muted">Total Users: {users?.total || userRows.length}</span>
								<Pagination className="pagination-gutter pagination-circle pagination mb-0">
									<Pagination.Prev
										disabled={currentPage === 1}
										onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
									/>
									{Array.from({ length: totalPages }, (_, i) => (
										<Pagination.Item
											key={i + 1}
											active={i + 1 === currentPage}
											onClick={() => setCurrentPage(i + 1)}
										>
											{i + 1}
										</Pagination.Item>
									))}
									<Pagination.Next
										disabled={currentPage === totalPages}
										onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
									/>
								</Pagination>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default User;
